import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import ClientTable from "../components/ClientTable";
import { getPageName, getUiInterface } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef, GridSortModel } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import { useClient } from "../../../apis/client/ClientProvider";
import { getClientsPaginated } from "../../../services/clientService";
import { exportarParaExcel } from "../../../utils/exportarExcel";
import ExportExcelButton from "../../../components/ExportExcelButton";
import Paginacao from "../../../types/paginacao";
import Client from "../../../types/client";
import { FaEdit } from "react-icons/fa";

const ClientView: React.FC = () => {
    const { t } = useTranslation();
    const { clients, apiResponse, fetchClient } = useClient();
    const navigate = useNavigate();
    const location = useLocation();
    const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 40,
        page: 0,
    });

    const [paginacao, setPaginacao] = React.useState({
        pageNum: 0,
        quantityPerPage: 40
    } as Paginacao);

    const [sortModel, setSortModel] = React.useState<GridSortModel>([]);

    useEffect(() => {
        const newPaginacao: Paginacao = {
            ...paginacao,
            pageNum: paginationModel.page,
            quantityPerPage: paginationModel.pageSize,
            sort: sortModel[0]?.field,
            order: sortModel[0]?.sort ?? undefined,
        };
        setPaginacao(newPaginacao);
        fetchClient(newPaginacao);
    }, [paginationModel, sortModel]);

    useEffect(() => {
        if (location.state?.message) {
            setAlert({ message: location.state.message, variant: location.state.variant });

            navigate(location.pathname, { replace: true });

            const timeout = setTimeout(() => setAlert(null), 5000);
            return () => clearTimeout(timeout);
        }
    }, [location, navigate]);

    const [exportando, setExportando] = useState(false);

    const handleExportarExcel = async () => {
        setExportando(true);

        try {
            const resposta = await getClientsPaginated({
                pageNum: 0,
                quantityPerPage: 100000,
                sort: sortModel[0]?.field,
                order: sortModel[0]?.sort,
            } as Paginacao);

            const linhas = (resposta?.data?.values ?? []).map((row) => ({
                [t('client.fields.id')]: row.id,
                [t('client.fields.cnpj')]: row.cnpj,
                [t('client.fields.name')]: row.name,
                [t('client.fields.color')]: row.color ? `#${row.color}` : '',
            }));

            if (!exportarParaExcel(linhas, 'client')) {
                setAlert({ message: getUiInterface('exportEmptyResult'), variant: 'warning' });
            }
        } catch (error) {
            setAlert({ message: getUiInterface('exportError'), variant: 'danger' });
        } finally {
            setExportando(false);
        }
    };

    const navItems: NavBarItem[] = [
        { label: t('client.labels.home'), route: "/", iconName: "house-door" },
        { label: t('client.labels.new'), route: "/Client/create", iconName: "plus-circle" },
    ];

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: t('client.fields.id'),
            width: 80,
            renderCell: (params) => (
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    color: "black",
                    cursor: "pointer"
                }}
                onClick={() => navigate(`/client/${params.row.id}`)}>
                    <FaEdit className="text-primary" style={{ marginRight: "6px", marginLeft: "2px" }}/>
                    {params.row.id}
                </span>
            ),
        },
        { field: "cnpj", headerName: t('client.fields.cnpj'), width: 200 },
        { field: "name", headerName: t('client.fields.name'), width: 350 },
        {
            field: "color",
            headerName: t('client.fields.color'),
            width: 150,
            renderCell: (params) => (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    <div style={{
                        display: "inline-block",
                        backgroundColor: `#${params.row.color}`,
                        borderRadius: "4px",
                        border: "1px solid black",
                        width: "24px",
                        height: "24px",
                        marginRight: "4px"
                    }}></div>
                    <span
                        style={{
                            display: "inline-block"
                        }}
                    >
                        {(params.row.color && params.row.color.length === 6 ) ? `#${params.row.color}` : "#"}
                    </span>
                </div>
            )
        },
    ];

    return (
          <Container fluid style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getPageName("client")}</h2>
            <div className="mb-3"></div>
            <div>
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}
        </div>
            <div className="d-flex justify-content-end mb-2">
                <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
                <ClientTable
                    rows={clients || [] as Client[]}
                    columns={columns}
                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                    apiResponse={apiResponse}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                />
            </div>
        </Container>
    );
};

export default ClientView;